/**
 * Centralized error tracking and logging utility
 * Integrates with Sentry and provides structured logging
 */
/* eslint-disable no-console */

import * as Sentry from '@sentry/react';
import { logFrontendError } from './errorReporting';

export interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  additionalData?: Record<string, unknown>;
}

export class ErrorTracker {
  private static instance: ErrorTracker;
  private isInitialized = false;
  private sentryAvailable = false;

  private constructor() {}

  static getInstance(): ErrorTracker {
    if (!ErrorTracker.instance) {
      ErrorTracker.instance = new ErrorTracker();
    }
    return ErrorTracker.instance;
  }

  initialize() {
    if (this.isInitialized) return;

    // Check if Sentry DSN is configured
    const sentryDsn = import.meta.env.VITE_SENTRY_DSN || import.meta.env.NEXT_PUBLIC_SENTRY_DSN;
    
    if (sentryDsn) {
      try {
        // Initialize Sentry with optimized configuration
        Sentry.init({
          dsn: sentryDsn,
          environment: import.meta.env.MODE || 'development',
          
          // Setting this option to true will send default PII data to Sentry
          sendDefaultPii: true,
          
          integrations: [
            // Browser tracing for performance monitoring
            Sentry.browserTracingIntegration(),
            
            // Session replay with privacy settings
            Sentry.replayIntegration({
              // Mask all text content for privacy
              maskAllText: true,
              blockAllMedia: true,
              
              // Network details - only capture from our domains
              networkDetailAllowUrls: [
                window.location.origin,
                /^https:\/\/.*\.supabase\.co/,
              ],
              
              // Performance optimization - don't capture request bodies
              networkCaptureBodies: false,
            }),
          ],
          
          // Tracing
          tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in prod, 100% in dev
          tracePropagationTargets: ["localhost", /^https:\/\/.*\.vercel\.app/],
          
          // Session Replay - Optimized sampling
          replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in prod, 100% in dev
          replaysOnErrorSampleRate: 1.0, // Always capture replays on errors
          
          // Enable logs to be sent to Sentry
          enableLogs: true,
          
          // Transport options - limit retries to reduce console spam
          transportOptions: {
            maxRetries: 3,
          },
          
          // Filter out noise
          beforeSend(event, hint) {
            // Allow errors through in development when VITE_ENABLE_SENTRY is explicitly set to 'true'
            // For testing, be permissive - we can restrict later
            const sentryEnabled = import.meta.env.VITE_ENABLE_SENTRY === 'true';
            if (import.meta.env.DEV && !sentryEnabled) {
              console.log('[Sentry] Event captured (dev mode, not sent - set VITE_ENABLE_SENTRY=true to enable):', event);
              return null;
            }
            
            // Log when events are being sent (for debugging)
            if (import.meta.env.DEV && sentryEnabled) {
              console.log('[Sentry] ✅ Event being sent to Sentry:', {
                message: event.message || event.exception?.values?.[0]?.value,
                level: event.level,
              });
            }
            
            // Filter out CSP violations (we're fixing these separately)
            if (event.exception?.values?.[0]?.value?.includes('Content Security Policy')) {
              return null;
            }
            
            // Filter out browser extension errors
            if (event.exception?.values?.[0]?.stacktrace?.frames?.some(
              frame => frame.filename?.includes('extension://')
            )) {
              return null;
            }
            
            // Filter out known non-critical errors
            if (event.exception) {
              const errorMessage = event.exception.values?.[0]?.value || '';
              
              // Don't send user-triggered errors
              const userErrors = [
                'cancelled',
                'aborted',
                'user denied',
                'popup closed',
              ];
              
              if (userErrors.some(err => errorMessage.toLowerCase().includes(err))) {
                return null;
              }
            }
            
            // Add custom context
            event.tags = {
              ...event.tags,
              app_version: import.meta.env.VITE_APP_VERSION || 'unknown',
            };
            
            return event;
          },
          
          // Ignore common noisy errors
          ignoreErrors: [
            // Browser extensions
            'top.GLOBALS',
            'canvas.contentDocument',
            'MyApp_RemoveAllHighlights',
            'atomicFindClose',
            
            // Network errors (handled separately)
            'NetworkError',
            'Network request failed',
            'Failed to fetch',
            
            // ResizeObserver errors (harmless)
            'ResizeObserver loop limit exceeded',
            'ResizeObserver loop completed with undelivered notifications',
          ],
          
          // Don't capture console calls in production
          captureConsole: import.meta.env.DEV,
        });
        
        this.sentryAvailable = true;
        
        if (import.meta.env.DEV) {
          console.log('[ErrorTracker] ✅ Sentry initialized successfully');
        }
      } catch (error) {
        console.warn('[ErrorTracker] Sentry initialization failed:', error);
        this.sentryAvailable = false;
      }
    } else {
      // Sentry DSN not configured - silently use console + database logging only
      this.sentryAvailable = false;
    }

    this.isInitialized = true;
  }

  captureError(error: Error, context?: ErrorContext) {
    const errorMessage = error.message || String(error);
    const errorStack = error.stack;

    // Always log to console in development
    if (import.meta.env.DEV) {
      console.group('🔴 [ErrorTracker] Error Captured');
      console.error('Error:', error);
      console.log('Context:', context);
      console.groupEnd();
    }

    // Log to console in production (for now)
    if (import.meta.env.PROD) {
      console.error('[ErrorTracker]', errorMessage, context);
    }

    // Send to Sentry if available
    if (this.sentryAvailable) {
      try {
        Sentry.captureException(error, {
          tags: {
            component: context?.component || 'unknown',
            action: context?.action || 'unknown',
          },
          extra: {
            ...context?.additionalData,
            userId: context?.userId,
          },
        });
      } catch (sentryError) {
        console.warn('[ErrorTracker] Failed to send to Sentry:', sentryError);
      }
    }

    // Also log to database via existing errorReporting utility
    try {
      logFrontendError(error, {
        component: context?.component,
        action: context?.action,
        userId: context?.userId,
        ...context?.additionalData,
      });
    } catch (errorReportingError) {
      // Silently fail if errorReporting is not available
      if (import.meta.env.DEV) {
        console.warn('[ErrorTracker] Could not log to database:', errorReportingError);
      }
    }
  }

  captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info', context?: ErrorContext) {
    const logMethod = level === 'error' ? 'error' : level === 'warning' ? 'warn' : 'log';
    
    if (import.meta.env.DEV) {
      console[logMethod]('[ErrorTracker]', message, context);
    }

    if (import.meta.env.PROD && level === 'error') {
      console.error('[ErrorTracker]', message, context);
    }

    // Send to Sentry if available
    if (this.sentryAvailable && (level === 'error' || level === 'warning')) {
      try {
        Sentry.captureMessage(message, {
          level: level === 'error' ? 'error' : 'warning',
          tags: {
            component: context?.component || 'unknown',
            action: context?.action || 'unknown',
          },
          extra: {
            ...context?.additionalData,
            userId: context?.userId,
          },
        });
      } catch (sentryError) {
        console.warn('[ErrorTracker] Failed to send to Sentry:', sentryError);
      }
    }
  }

  setUser(userId: string, email?: string, additionalData?: Record<string, unknown>) {
    if (this.sentryAvailable) {
      try {
        Sentry.setUser({
          id: userId,
          email: email,
          ...additionalData,
        });
      } catch (sentryError) {
        console.warn('[ErrorTracker] Failed to set Sentry user:', sentryError);
      }
    }
  }

  clearUser() {
    if (this.sentryAvailable) {
      try {
        Sentry.setUser(null);
      } catch (sentryError) {
        console.warn('[ErrorTracker] Failed to clear Sentry user:', sentryError);
      }
    }
  }
}

// Export singleton instance
export const errorTracker = ErrorTracker.getInstance();

// Convenience functions
export const captureError = (error: Error, context?: ErrorContext) => 
  errorTracker.captureError(error, context);

export const captureMessage = (message: string, level?: 'info' | 'warning' | 'error', context?: ErrorContext) => 
  errorTracker.captureMessage(message, level, context);
